import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Calendar, Clock, Check, ChevronLeft, ChevronRight, User, ArrowRight, ArrowLeft } from 'lucide-react';
import './ScheduleCallPopup.css';

const ScheduleCallPopup = ({ isOpen, onClose }) => {
  const popupRef = useRef(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // Track the current step (1, 2, or 3)
  
  // Step 1: User Information
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Step 2: Date Selection
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableDates, setAvailableDates] = useState([]);
  const [datesWithSlots, setDatesWithSlots] = useState(new Set()); // Track dates that have available time slots
  const [monthlyData, setMonthlyData] = useState(new Map()); // Cache monthly availability data with time slots
  const [loadingMonth, setLoadingMonth] = useState(false); // Loading state for month navigation
  const [cachedMonths, setCachedMonths] = useState(new Set()); // Track which months have been loaded
  const fetchingMonthRef = useRef(null); // Track which month is currently being fetched
  
  // Step 3: Time Selection and Confirmation
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [confirmedDetails, setConfirmedDetails] = useState(null);
  const [isBooking, setIsBooking] = useState(false);

  // API Integration Functions
  const bookAppointment = async () => {
    if (!selectedDate || !selectedTime || !firstName || !lastName || !email) {
      return;
    }

    setIsBooking(true);
    try {
      // Format date in local timezone to avoid date shifting
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      console.log('Booking appointment for date:', dateString, 'time:', selectedTime);
      
      // Import authenticated API function dynamically
      const { bookAuthenticatedAppointment } = await import('../utils/authApi');
      
      const response = await bookAuthenticatedAppointment({
        first_name: firstName,
        last_name: lastName,
        email: email,
        date: dateString,
        time: selectedTime,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });

      if (response.status === 'success') {
        // Format the date for display
        const formattedDate = selectedDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
        
        // Set confirmed details for display in confirmation message
        setConfirmedDetails({
          firstName: firstName,
          lastName: lastName,
          date: formattedDate,
          time: selectedTime,
          email: email,
          meetLink: response.meet_link,
          eventId: response.event_id
        });
        
        // Show confirmation message
        setIsConfirmed(true);
      } else {
        alert('Failed to schedule appointment. Please try again.');
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      alert(error.message || 'Failed to schedule appointment. Please try again.');
    } finally {
      setIsBooking(false);
    }
  };
  
  // Generate calendar days for the current month
  const generateCalendarDays = useCallback(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the day of the week for the first day (0 = Sunday, 6 = Saturday)
    const firstDayOfWeek = firstDay.getDay();
    
    // Array to hold all calendar days
    const days = [];
    
    // Add empty slots for days before the first day of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({
        date: null,
        day: '',
        dateNum: '',
        month: '',
        isCurrentMonth: false,
        available: false
      });
    }
    
    // Add days of the current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      const today = new Date();
      
      // Set time to start of day for accurate date comparison
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      // Check if date is tomorrow or later (can't book today)
      const isBookableDate = dateStart > todayStart;
      
      // Check if this date has available time slots
      const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const hasAvailableSlots = datesWithSlots.has(dateString);
      
      days.push({
        date: date,
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dateNum: i,
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        isCurrentMonth: true,
        available: isBookableDate && hasAvailableSlots // Must be future date AND have available slots
      });
    }
    
    // Always ensure we have exactly 6 rows (42 days) for consistent sizing
    const totalDaysToShow = 6 * 7; // 6 rows, 7 days per row
    const remainingSlots = totalDaysToShow - days.length;
    
    if (remainingSlots > 0) {
      for (let i = 1; i <= remainingSlots; i++) {
        days.push({
          date: null,
          day: '',
          dateNum: '',
          month: '',
          isCurrentMonth: false,
          available: false
        });
      }
    }
    
    return days;
  }, [currentMonth, datesWithSlots]);
  
  // Update available dates when month changes
  useEffect(() => {
    setAvailableDates(generateCalendarDays());
  }, [currentMonth, generateCalendarDays]);

  // Update available dates when month changes
  useEffect(() => {
    setAvailableDates(generateCalendarDays());
  }, [currentMonth, generateCalendarDays]);

  // Fetch availability when month changes or popup opens
  useEffect(() => {
    if (!isOpen) return;
    
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const monthKey = `${year}-${month}`;
    
    console.log(`useEffect triggered for month ${monthKey}, cached: ${cachedMonths.has(monthKey)}, fetching: ${fetchingMonthRef.current}`);
    
    // Only fetch if not already cached and not currently being fetched
    if (!cachedMonths.has(monthKey) && fetchingMonthRef.current !== monthKey) {
      // Fetch logic directly in useEffect to avoid dependency issues
      const fetchData = async () => {
        console.log(`fetchData called for ${monthKey}, currently fetching: ${fetchingMonthRef.current}`);
        
        // Double-check to prevent race conditions
        if (fetchingMonthRef.current === monthKey) {
          console.log(`Month ${monthKey} is already being fetched, skipping`);
          return;
        }
        
        // Mark this month as being fetched
        fetchingMonthRef.current = monthKey;
        setLoadingMonth(true);
        
        try {
          console.log(`Starting API call for monthly availability: ${year}-${month}`);
          
          // Import authenticated API function dynamically
          const { getAuthenticatedMonthlyAvailability } = await import('../utils/authApi');
          
          const response = await getAuthenticatedMonthlyAvailability(
            year, 
            month, 
            Intl.DateTimeFormat().resolvedOptions().timeZone
          );

          console.log(`API call completed for ${monthKey}`);

          // Process the response
          setDatesWithSlots(prevDates => {
            const allDatesWithSlots = new Set(prevDates);
            if (response.days) {
              response.days.forEach(day => {
                if (day.has_slots && day.slot_count > 0) {
                  allDatesWithSlots.add(day.date);
                }
              });
            }
            return allDatesWithSlots;
          });

          setMonthlyData(prevData => {
            const allMonthlyData = new Map(prevData);
            if (response.days) {
              response.days.forEach(day => {
                if (day.has_slots && day.slot_count > 0 && day.time_slots) {
                  allMonthlyData.set(day.date, day.time_slots);
                }
              });
            }
            return allMonthlyData;
          });

          setCachedMonths(prevCached => {
            const allCachedMonths = new Set(prevCached);
            allCachedMonths.add(monthKey);
            console.log(`Successfully cached data for month ${monthKey} with ${response.days?.filter(d => d.has_slots).length || 0} available dates`);
            return allCachedMonths;
          });
          
        } catch (error) {
          console.error('Error fetching month availability:', error);
        } finally {
          // Clear the fetching reference and loading state
          console.log(`Finished processing ${monthKey}, clearing fetching reference`);
          fetchingMonthRef.current = null;
          setLoadingMonth(false);
        }
      };
      
      fetchData();
    }
  }, [currentMonth, isOpen, cachedMonths]);

  // Update calendar when datesWithSlots changes (after monthly availability is fetched)
  useEffect(() => {
    setAvailableDates(generateCalendarDays());
  }, [datesWithSlots, generateCalendarDays]);

  // Get time slots from cached data when a date is selected
  useEffect(() => {
    if (selectedDate) {
      setLoadingTimeSlots(true);
      
      // Format date to match the cached data key
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      console.log('Getting time slots from cache for date:', dateString);
      
      // Get time slots from cached monthly data
      const cachedTimeSlots = monthlyData.get(dateString);
      if (cachedTimeSlots) {
        setAvailableTimeSlots(cachedTimeSlots);
        console.log(`Found ${cachedTimeSlots.length} cached time slots for ${dateString}`);
      } else {
        console.log(`No cached time slots found for ${dateString}`);
        setAvailableTimeSlots([]);
      }
      
      setLoadingTimeSlots(false);
    }
  }, [selectedDate, monthlyData]);
  
  // Check if we can navigate to previous month (can't go before current month)
  const canGoToPreviousMonth = () => {
    const today = new Date();
    const currentMonthYear = today.getFullYear();
    const currentMonthMonth = today.getMonth();
    
    const displayedMonthYear = currentMonth.getFullYear();
    const displayedMonthMonth = currentMonth.getMonth();
    
    // Can go back if displayed month is after current month
    return displayedMonthYear > currentMonthYear || 
           (displayedMonthYear === currentMonthYear && displayedMonthMonth > currentMonthMonth);
  };
  
  // Navigate to previous month (with restrictions)
  const goToPreviousMonth = () => {
    if (!canGoToPreviousMonth() || loadingMonth) return;
    
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(prevMonth.getMonth() - 1);
      return newMonth;
    });
  };
  
  // Navigate to next month
  const goToNextMonth = () => {
    if (loadingMonth) return;
    
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(prevMonth.getMonth() + 1);
      return newMonth;
    });
  };
  
  // Check if a date is today
  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const handleClose = useCallback(() => {
    setIsClosing(true);
    // Delay actual closing to allow animation to complete
    setTimeout(() => {
      onClose();
    }, 180); // Slightly shorter than animation to ensure smooth transition
  }, [onClose]);

  const handleDateSelect = (dateObj) => {
    if (dateObj && dateObj.date && dateObj.available) {
      setSelectedDate(dateObj.date);
      setSelectedTime(null); // Reset time selection when date changes
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleFirstNameChange = (e) => {
    const value = e.target.value;
    setFirstName(value);
    
    if (!value) {
      setFirstNameError('First name is required');
    } else {
      setFirstNameError('');
    }
  };
  
  const handleLastNameChange = (e) => {
    const value = e.target.value;
    setLastName(value);
    
    if (!value) {
      setLastNameError('Last name is required');
    } else {
      setLastNameError('');
    }
  };
  
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    if (!value) {
      setEmailError('Email is required');
    } else if (!validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  // Navigate to the next step
  const goToNextStep = () => {
    if (currentStep === 1) {
      // Validate user information before proceeding
      let isValid = true;
      
      if (!firstName) {
        setFirstNameError('First name is required');
        isValid = false;
      }
      
      if (!lastName) {
        setLastNameError('Last name is required');
        isValid = false;
      }
      
      if (!email) {
        setEmailError('Email is required');
        isValid = false;
      } else if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        isValid = false;
      }
      
      if (!isValid) return;
    }
    
    if (currentStep === 2 && !selectedDate) {
      // Validate date selection before proceeding
      alert('Please select a date before proceeding');
      return;
    }
    
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  // Navigate to the previous step
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      // If moving back from step 3 to step 2, reset the time selection
      if (currentStep === 3) {
        setSelectedTime(null);
      }
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleConfirm = async () => {
    if (!selectedTime) {
      alert('Please select a time');
      return;
    }
    
    await bookAppointment();
  };

  // Handle opening and closing animations
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setShouldRender(true);
      // Reset state when opening - but keep cache to avoid refetching
      setCurrentStep(1);
      setIsConfirmed(false);
      setConfirmedDetails(null);
      setSelectedTime(null);
      setAvailableTimeSlots([]);
      setIsBooking(false);
      setLoadingMonth(false); // Reset loading state
      // Don't reset cache and dates here to avoid refetching
    } else if (shouldRender) {
      // Start closing animation
      setIsClosing(true);
      // Remove component after animation completes
      const timer = setTimeout(() => {
        setShouldRender(false);
        // Reset all state when popup closes
        setCurrentStep(1);
        setFirstName('');
        setLastName('');
        setEmail('');
        setFirstNameError('');
        setLastNameError('');
        setEmailError('');
        setSelectedDate(null);
        setSelectedTime(null);
        setAvailableTimeSlots([]);
        setLoadingTimeSlots(false);
        setIsConfirmed(false);
        setConfirmedDetails(null);
        setIsBooking(false);
        setDatesWithSlots(new Set());
        setMonthlyData(new Map());
        setCachedMonths(new Set());
        setLoadingMonth(false);
        fetchingMonthRef.current = null;
      }, 200); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleClose();
      }
    };

    // Add event listener when the popup is open
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up the event listener when component unmounts or popup closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);
  
  if (!shouldRender) return null;

  return (
    <div className={`schedule-popup-overlay ${isClosing ? 'closing' : ''}`}>
      <div className={`schedule-popup ${isClosing ? 'closing' : ''}`} ref={popupRef}>
        <div className="schedule-popup-header">
          <h2>Schedule a Call</h2>
          <div className="step-indicator">
            <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>1</div>
            <div className="step-connector"></div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>2</div>
            <div className="step-connector"></div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
          </div>
          <button className="close-button" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>
        
        <div className="schedule-popup-content">
          {/* Step 1: User Information */}
          {currentStep === 1 && (
            <div className="step-content">
              <div className="schedule-section">
                <div className="section-header">
                  <User size={18} />
                  <h3>Your Information</h3>
                </div>
                
                <div className="input-container">
                  <label htmlFor="firstName">First Name</label>
                  <input 
                    id="firstName"
                    type="text" 
                    className={`text-input ${firstNameError ? 'error' : ''}`}
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={handleFirstNameChange}
                  />
                  {firstNameError && <div className="input-error">{firstNameError}</div>}
                </div>
                
                <div className="input-container">
                  <label htmlFor="lastName">Last Name</label>
                  <input 
                    id="lastName"
                    type="text" 
                    className={`text-input ${lastNameError ? 'error' : ''}`}
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={handleLastNameChange}
                  />
                  {lastNameError && <div className="input-error">{lastNameError}</div>}
                </div>
                
                <div className="input-container">
                  <label htmlFor="email">Email Address</label>
                  <input 
                    id="email"
                    type="email" 
                    className={`text-input ${emailError ? 'error' : ''}`}
                    placeholder="Enter your email address"
                    value={email}
                    onChange={handleEmailChange}
                  />
                  {emailError && <div className="input-error">{emailError}</div>}
                </div>
              </div>
              
              <button 
                className="step-button next-button active"
                onClick={goToNextStep}
              >
                Next Step
                <ArrowRight size={18} />
              </button>
            </div>
          )}
          
          {/* Step 2: Date Selection */}
          {currentStep === 2 && (
            <div className="step-content">
              <div className="schedule-section">
                <div className="section-header">
                  <Calendar size={18} />
                  <h3>Select a Date</h3>
                </div>
                <div className="calendar-header">
                  <button 
                    className={`month-nav-button ${!canGoToPreviousMonth() || loadingMonth ? 'disabled' : ''}`} 
                    onClick={goToPreviousMonth}
                    disabled={!canGoToPreviousMonth() || loadingMonth}
                  >
                    {loadingMonth ? (
                      <div className="loading-spinner small"></div>
                    ) : (
                      <ChevronLeft size={18} />
                    )}
                  </button>
                  <div className="current-month">
                    {loadingMonth ? (
                      <div className="month-loading">
                        <div className="loading-spinner small"></div>
                        <span>Loading...</span>
                      </div>
                    ) : (
                      currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                    )}
                  </div>
                  <button 
                    className={`month-nav-button ${loadingMonth ? 'disabled' : ''}`} 
                    onClick={goToNextMonth}
                    disabled={loadingMonth}
                  >
                    {loadingMonth ? (
                      <div className="loading-spinner small"></div>
                    ) : (
                      <ChevronRight size={18} />
                    )}
                  </button>
                </div>
                <div className="weekday-header">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                    <div key={index} className="weekday-label">{day}</div>
                  ))}
                </div>
                <div className="calendar-grid">
                  {availableDates.map((dateObj, index) => {
                    // Check if this date has available time slots
                    const dateString = dateObj.date ? 
                      `${dateObj.date.getFullYear()}-${String(dateObj.date.getMonth() + 1).padStart(2, '0')}-${String(dateObj.date.getDate()).padStart(2, '0')}` 
                      : null;
                    const hasAvailableSlots = dateString && datesWithSlots.has(dateString);
                    
                    return (
                      <button 
                        key={index}
                        className={`calendar-day 
                          ${!dateObj.isCurrentMonth ? 'outside-month' : ''} 
                          ${dateObj.date && isToday(dateObj.date) ? 'today' : ''} 
                          ${!dateObj.available ? 'unavailable' : ''} 
                          ${selectedDate && dateObj.date && selectedDate.toDateString() === dateObj.date.toDateString() ? 'selected' : ''}`}
                        onClick={() => handleDateSelect(dateObj)}
                        disabled={!dateObj.isCurrentMonth || !dateObj.available}
                      >
                        {dateObj.dateNum}
                        {hasAvailableSlots && dateObj.isCurrentMonth && <span className="available-indicator"></span>}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div className="step-navigation">
                <button 
                  className="step-button prev-button"
                  onClick={goToPreviousStep}
                >
                  <ArrowLeft size={18} />
                  Previous
                </button>
                
                <button 
                  className={`step-button next-button ${selectedDate ? 'active' : 'disabled'}`}
                  onClick={goToNextStep}
                  disabled={!selectedDate}
                >
                  Next Step
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}
          
          {/* Step 3: Time Selection and Confirmation */}
          {currentStep === 3 && !isConfirmed && (
            <div className="step-content">
              <div className="schedule-section">
                <div className="section-header">
                  <Clock size={18} />
                  <h3>Select a Time</h3>
                </div>
                
                {loadingTimeSlots ? (
                  <div className="loading-message">
                    <p>Loading available time slots...</p>
                  </div>
                ) : availableTimeSlots.length > 0 ? (
                  <div className="time-grid">
                    {availableTimeSlots.map((slot, index) => (
                      <button 
                        key={index}
                        className={`time-button ${selectedTime === slot.display_time ? 'selected' : ''}`}
                        onClick={() => setSelectedTime(slot.display_time)}
                      >
                        {slot.display_time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="no-slots-message">
                    <p>No available time slots for this date. Please select a different date.</p>
                  </div>
                )}
              </div>
              
              <div className="appointment-summary">
                <h3>Appointment Summary</h3>
                <div className="summary-details">
                  <div className="summary-item">
                    <span className="summary-label">Name:</span>
                    <span className="summary-value">{firstName} {lastName}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Email:</span>
                    <span className="summary-value">{email}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Date:</span>
                    <span className="summary-value">
                      {selectedDate?.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Time:</span>
                    <span className="summary-value">{selectedTime || "Not selected yet"}</span>
                  </div>
                </div>
              </div>
              
              <div className="step-navigation">
                <button 
                  className="step-button prev-button"
                  onClick={goToPreviousStep}
                  disabled={isBooking}
                >
                  <ArrowLeft size={18} />
                  Previous
                </button>
                
                <button 
                  className={`step-button confirm-button ${selectedTime && !isBooking ? 'active' : 'disabled'}`}
                  onClick={handleConfirm}
                  disabled={!selectedTime || isBooking}
                >
                  {isBooking ? (
                    <>
                      <div className="loading-spinner"></div>
                      Booking...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Confirm Appointment
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          
          {/* Confirmation Message */}
          {isConfirmed && (
            <div className="confirmation-message">
              <div className="confirmation-icon">
                <Check size={24} />
              </div>
              <h3>Appointment Confirmed!</h3>
              <p>Thank you, {confirmedDetails?.firstName}!</p>
              <p>Your call is scheduled for {confirmedDetails?.date} at {confirmedDetails?.time}</p>
              
              <div className="email-notification">
                <p>A confirmation email with meeting details has been sent to {confirmedDetails?.email}</p>
              </div>
              
              <button 
                className="close-confirmation-button"
                onClick={handleClose}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleCallPopup;